import React, { useState, useCallback, useRef, useEffect } from "react";
import "./Timeline.scss";
import LottieIcon from "../../components/common/LottieIcon";
import { getIconCategoryClass } from "../../components/common/iconCategory";

const Timeline = ({ panels, selectedItem, onSelectItem, zoomLevel, onZoomIn, onZoomOut, onComponentExpansionChange, stackingOrder = [], previewFlow }) => {
  const dragDataRef = useRef(null);
  const panelsRef = useRef(panels);
  const handlersRef = useRef({ move: null, up: null });
  const restoreCursorRef = useRef(null);
  const cursorStyleRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const isHandleActiveRef = useRef(false);
  const cursorIntervalRef = useRef(null);

  const setBodyCursor = (cursor) => {
    if (restoreCursorRef.current === null) {
      restoreCursorRef.current = document.body.style.cursor;
    }
    document.body.style.cursor = cursor;
  };

  const resetBodyCursor = () => {
    console.log('[RESET] resetBodyCursor called!');
    console.trace('[RESET] Called from:');
    if (restoreCursorRef.current !== null) {
      document.body.style.cursor = restoreCursorRef.current;
      restoreCursorRef.current = null;
    }
  };

  const handleMouseEnterHandle = (e) => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    
    if (cursorIntervalRef.current) {
      clearInterval(cursorIntervalRef.current);
    }
    
    isHandleActiveRef.current = true;
    
    const forceCursor = () => {
      document.documentElement.classList.add('timeline-resize-active');
      document.body.classList.add('timeline-resize-active');
      document.body.style.cursor = 'ew-resize';
      document.documentElement.style.cursor = 'ew-resize';
      
      if (e && e.currentTarget) {
        e.currentTarget.style.cursor = 'ew-resize';
      }
    };
    
    forceCursor();
    cursorIntervalRef.current = setInterval(forceCursor, 10);
  };

  const handleMouseLeaveHandle = (e) => {
    if (e && e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return;
      }
    }
    
    if (cursorIntervalRef.current) {
      clearInterval(cursorIntervalRef.current);
      cursorIntervalRef.current = null;
    }
    
    if (!dragDataRef.current) {
      leaveTimeoutRef.current = setTimeout(() => {
        isHandleActiveRef.current = false;
        document.documentElement.classList.remove('timeline-resize-active');
        document.body.classList.remove('timeline-resize-active');
        document.body.style.cursor = '';
        document.documentElement.style.cursor = '';
      }, 50);
    }
  };
  
  // Convert steps with components to timeline tracks
  const generateTimelineTracks = () => {
    if (!panels || panels.length === 0) return [];
    
    const sortedPanels = [...panels].sort((a, b) => a.x - b.x);
    const allClips = [];
    
    // Iterate through each step and its components
    sortedPanels.forEach((panel, stepIndex) => {
      if (panel.components && panel.components.length > 0) {
        panel.components.forEach((component) => {
          const stepLength = component.stepLength || 1;
          const startStep = stepIndex;
          const endStep = stepIndex + stepLength - 1; // stepLength of 1 means single step (start === end)
          
          allClips.push({
            id: component.id,
            stepId: panel.id,
            stepName: panel.label,
            stepIndex: stepIndex,
            component: component,
            panel: panel,
            startStep: startStep,
            endStep: endStep,
            trackRow: null // Will be assigned
          });
        });
      }
    });
    
    // Iterative row assignment considering cascading constraints
    // If a clip is pushed down by a better clip at one position, 
    // all worse clips at overlapping positions must also be pushed down
    
    // Initialize all clips to row 0
    allClips.forEach(clip => {
      clip.trackRow = 0;
    });
    
    let changed = true;
    let iterations = 0;
    const maxIterations = 20;
    
    while (changed && iterations < maxIterations) {
      changed = false;
      iterations++;
      
      allClips.forEach(clip => {
        const clipFeatureId = clip.component.featureId || clip.component.campaignFeatureId;
        const clipStackingIndex = stackingOrder.findIndex(item => item.featureId === clipFeatureId);
        let newRow = 0;
        
        // Check each step this clip spans
        for (let step = clip.startStep; step <= clip.endStep; step++) {
          // Find all clips at this step with better stacking order
          allClips.forEach(otherClip => {
            if (otherClip.id === clip.id) return;
            
            // Is other clip at this step?
            if (step >= otherClip.startStep && step <= otherClip.endStep) {
              const otherFeatureId = otherClip.component.featureId || otherClip.component.campaignFeatureId;
              const otherStackingIndex = stackingOrder.findIndex(item => item.featureId === otherFeatureId);
              
              // Determine positioning based on stacking order
              let shouldBeBelow = false;
              
              if (clipStackingIndex !== -1 && otherStackingIndex !== -1) {
                // Both in stacking order
                if (otherStackingIndex < clipStackingIndex) {
                  // Other has better stacking order - go below
                  shouldBeBelow = true;
                } else if (otherStackingIndex === clipStackingIndex) {
                  // Same stacking order (same feature type, different instances)
                  // Avoid collision - if other is already at same row or higher, move down
                  if (otherClip.trackRow >= clip.trackRow) {
                    shouldBeBelow = true;
                  }
                }
              } else if (clipStackingIndex === -1 && otherStackingIndex !== -1) {
                // Only other clip is in stacking order - it gets priority (goes on top)
                shouldBeBelow = true;
              } else if (clipStackingIndex !== -1 && otherStackingIndex === -1) {
                // Only current clip is in stacking order - it gets priority (stays on top)
                shouldBeBelow = false;
              } else {
                // Neither in stacking order - avoid collision by checking if other is already placed
                // If other clip is at the same row or higher, move down
                if (otherClip.trackRow >= clip.trackRow) {
                  shouldBeBelow = true;
                }
              }
              
              if (shouldBeBelow) {
                newRow = Math.max(newRow, otherClip.trackRow + 1);
              }
            }
          });
        }
        
        if (newRow !== clip.trackRow) {
          clip.trackRow = newRow;
          changed = true;
        }
      });
      
      // After collision resolution, try to move clips up to fill gaps
      allClips.forEach(clip => {
        if (clip.trackRow === 0) return; // Already at top
        
        // Try each row from 0 to current row - 1
        for (let testRow = 0; testRow < clip.trackRow; testRow++) {
          let canFit = true;
          
          // Check if this clip can fit at testRow without collisions
          for (let step = clip.startStep; step <= clip.endStep; step++) {
            allClips.forEach(otherClip => {
              if (otherClip.id === clip.id) return;
              if (otherClip.trackRow !== testRow) return;
              
              // Does other clip overlap at this step?
              if (step >= otherClip.startStep && step <= otherClip.endStep) {
                canFit = false;
              }
            });
          }
          
          // If we can fit at this row, move up
          if (canFit) {
            clip.trackRow = testRow;
            changed = true;
            break; // Found the lowest available row
          }
        }
      });
    }
    
    // Sort clips by their assigned row, then by start step
    allClips.sort((a, b) => {
      if (a.trackRow !== b.trackRow) return a.trackRow - b.trackRow;
      return a.startStep - b.startStep;
    });
    
    return allClips;
  };

  const tracks = generateTimelineTracks();

  // Keep panels ref updated
  useEffect(() => {
    panelsRef.current = panels;
  }, [panels]);

  // Generate ruler marks based on step positions
  const generateRulerMarks = () => {
    if (!panels || panels.length === 0) return [];
    
    const sortedPanels = [...panels].sort((a, b) => a.x - b.x);
    
    // Remove duplicate panels (same stepId or id) - can happen when components are expanded
    const uniquePanels = [];
    const seenIds = new Set();
    
    sortedPanels.forEach(panel => {
      const panelId = panel.stepId || panel.id;
      if (!seenIds.has(panelId)) {
        seenIds.add(panelId);
        uniquePanels.push(panel);
      }
    });
    
    return uniquePanels.map((panel, index) => ({
      ...panel,
      sequenceNumber: index + 1,
      leftPosition: index * 160 + 20,
      clipWidth: 140
    }));
  };

  const rulerMarks = generateRulerMarks();

  const handlePanelClick = (panel) => {
    onSelectItem?.(panel);
  };

  // Handle mouse move during drag
  const handleMouseMove = (e) => {
    if (!dragDataRef.current) return;
    
    const dragData = dragDataRef.current;
    const deltaX = e.clientX - dragData.startX;
    const stepWidth = 160; // Same as in CSS
    const stepsChanged = Math.round(deltaX / stepWidth);
    
    let newStartStep = dragData.originalStartStep;
    let newEndStep = dragData.originalEndStep;
    
    if (dragData.edge === 'left') {
      // Dragging left edge: When we shrink from the left, we're removing leftmost steps
      // The component should stay anchored to its RIGHT edge, not move
      // So we adjust both start and end by the same amount
      const stepsToRemove = stepsChanged; // positive = remove from left, negative = add to left
      
      newStartStep = Math.max(0, dragData.originalStartStep + stepsToRemove);
      // Keep the end at the same position (don't let start go past end)
      newStartStep = Math.min(newStartStep, dragData.originalEndStep);
      
      // End doesn't change when dragging left edge
      newEndStep = dragData.originalEndStep;
    } else {
      // Dragging right edge: positive deltaX = drag right (increase end), negative = drag left (decrease end)
      // Allow expansion beyond existing panels - NodeEditor will create placeholder steps
      newEndStep = Math.max(dragData.originalStartStep, dragData.originalEndStep + stepsChanged); // Can't go before start
    }
    
    // Ensure valid range
    if (newStartStep <= newEndStep) {
      const newStepLength = newEndStep - newStartStep + 1;
      const originalStepIndex = dragData.track.stepIndex;
      
      // Only update if something changed
      if (newStartStep !== originalStepIndex || newStepLength !== (dragData.track.component.stepLength || 1)) {
        // Get the step ID for the new home step (may be undefined if expanding beyond existing panels)
        const newHomeStepId = panelsRef.current[newStartStep]?.id || dragData.track.stepId;
        
        // Notify parent to update the component's stepLength
        // For cascade operations (shrink from left when alone), end the drag immediately
        const shouldEndDrag = newStartStep > originalStepIndex && dragData.track.panel.components.length === 1;
        
        if (onComponentExpansionChange) {
          onComponentExpansionChange(
            dragData.track.component.id, 
            dragData.track.stepId, 
            newStepLength,
            newHomeStepId // Pass step ID instead of index
          );
        }
        
        // End drag immediately if this was a cascade operation to prevent stale references
        if (shouldEndDrag) {
          dragDataRef.current = null;
        }
      }
    }
  };

  // Handle mouse up during drag
  const handleMouseUp = () => {
    isHandleActiveRef.current = false;
    
    // Remove cursor override
    if (cursorStyleRef.current) {
      cursorStyleRef.current.textContent = '';
    }
    
    if (!dragDataRef.current) return;
    
    dragDataRef.current = null;
    
    // Remove event listeners using stored references
    if (handlersRef.current.move) {
      document.removeEventListener('mousemove', handlersRef.current.move);
      handlersRef.current.move = null;
    }
    if (handlersRef.current.up) {
      document.removeEventListener('mouseup', handlersRef.current.up);
      handlersRef.current.up = null;
    }
  };

  // Handle drag start for resizing clips
  const handleDragStart = (e, track, edge) => {
    const stepLength = track.component.stepLength || 1;
    const startStep = track.stepIndex;
    const endStep = track.stepIndex + stepLength - 1;
    
    e.preventDefault();
    e.stopPropagation();
    
    isHandleActiveRef.current = true;
    
    // Inject cursor override style
    if (!cursorStyleRef.current) {
      cursorStyleRef.current = document.createElement('style');
      cursorStyleRef.current.id = 'timeline-cursor-override';
      document.head.appendChild(cursorStyleRef.current);
    }
    cursorStyleRef.current.textContent = `
      * { cursor: ew-resize !important; }
    `;
    
    dragDataRef.current = {
      clipId: track.id,
      track: track,
      edge: edge, // 'left' or 'right'
      originalStartStep: startStep,
      originalEndStep: endStep,
      startX: e.clientX
    };
    
    // Remove any existing listeners first
    if (handlersRef.current.move) {
      document.removeEventListener('mousemove', handlersRef.current.move);
    }
    if (handlersRef.current.up) {
      document.removeEventListener('mouseup', handlersRef.current.up);
    }
    
    // Create and store new handler references
    handlersRef.current.move = handleMouseMove;
    handlersRef.current.up = handleMouseUp;
    
    document.addEventListener('mousemove', handlersRef.current.move);
    document.addEventListener('mouseup', handlersRef.current.up);
  };

  // Calculate clip width and position based on stepLength
  const getClipDimensions = (track) => {
    const stepLength = track.component.stepLength || 1;
    const baseLeft = track.stepIndex * 160 + 20;
    const width = stepLength * 160 - 20; // Span across multiple steps
    
    return { left: baseLeft, width };
  };

  // Calculate minimum width needed for all clips
  const calculateMinWidth = () => {
    if (panels.length === 0) return 0;
    // Width should be: (number of steps * step width) + padding
    return panels.length * 160 + 40;
  };

  const minWidth = calculateMinWidth();

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      if (handlersRef.current.move) {
        document.removeEventListener('mousemove', handlersRef.current.move);
      }
      if (handlersRef.current.up) {
        document.removeEventListener('mouseup', handlersRef.current.up);
      }
      resetBodyCursor();
    };
  }, []);

  return (
    <div className="timeline-container">{/* removed handle-active class since we're using ref now */}
      <div className="timeline-toolbar">
        <div className="timeline-title">
          <span>Timeline</span>
          <button className="play-button" onClick={previewFlow}>
            <span className="play-icon">▶</span>
            Play flow
          </button>
        </div>
        <div className="timeline-controls">
          <div className="zoom-control">
            <button className="zoom-out" onClick={onZoomOut}>-</button>
            <span className="zoom-level">{Math.round((zoomLevel ?? 1) * 100)}%</span>
            <button className="zoom-in" onClick={onZoomIn}>+</button>
          </div>
        </div>
      </div>
      
      <div className="timeline-content">
        <div className="timeline-ruler">
          <div className="step-names">
            {rulerMarks.map((mark, index) => (
              <div 
                key={`step-${mark.id}`} 
                className="step-name-header"
                style={{
                  position: 'absolute',
                  left: `${mark.leftPosition}px`,
                  width: `${mark.clipWidth}px`,
                }}
              >
                <span className="step-name-text">{mark.label}</span>
              </div>
            ))}
          </div>
          <div className="ruler-marks">
            {rulerMarks.map((mark, index) => (
              <div 
                key={mark.id} 
                className="ruler-mark"
                style={{
                  position: 'absolute',
                  left: `${mark.leftPosition + mark.clipWidth / 2}px`, // Center the number above the clip
                }}
              >
                <div className="ruler-number">{mark.sequenceNumber}</div>
                <div className="ruler-line"></div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="timeline-tracks">
          {tracks.length === 0 ? (
            <div className="timeline-empty">
              <div className="empty-message">
                No panels added yet. Add panels to the stage above to see them here.
              </div>
            </div>
          ) : (
            // Group tracks by trackRow
            (() => {
              const groupedTracks = {};
              tracks.forEach(track => {
                if (!groupedTracks[track.trackRow]) {
                  groupedTracks[track.trackRow] = [];
                }
                groupedTracks[track.trackRow].push(track);
              });
              
              return Object.keys(groupedTracks).sort((a, b) => Number(a) - Number(b)).map((rowNum) => {
                const rowTracks = groupedTracks[rowNum];
                const firstTrack = rowTracks[0];
                
                return (
                  <div key={`row-${rowNum}`} className={`timeline-track ${firstTrack.isFirstComponentInStep ? 'step-header' : 'component-track'}`}>
                    <div className="track-controls">
                      <div className="track-number">{Number(rowNum) + 1}</div>
                    </div>
                    <div className="track-content">
                      <div className="track-bars" style={{ minWidth: `${minWidth}px` }}>
                        {rowTracks.map((track) => {
                          const dimensions = getClipDimensions(track);
                          const isExpanded = (track.component.stepLength || 1) > 1;
                          
                          return (
                            <div
                              key={track.id}
                              className={`timeline-clip ${selectedItem?.id === track.component.id ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => handlePanelClick(track.component)}
                              style={{
                                left: `${dimensions.left}px`,
                                width: `${dimensions.width}px`,
                              }}
                            >
                              {/* Left drag handle */}
                              <div 
                                className="clip-resize-handle left"
                                onMouseDown={(e) => handleDragStart(e, track, 'left')}
                                onMouseEnter={(e) => handleMouseEnterHandle(e)}
                                onMouseLeave={(e) => handleMouseLeaveHandle(e)}
                                onMouseOver={(e) => {
                                  e.stopPropagation();
                                  handleMouseEnterHandle(e);
                                }}
                                style={{ cursor: 'ew-resize', pointerEvents: 'auto', zIndex: 999, position: 'absolute' }}
                              />

                              <div className="clip-content">
                                <span className={`clip-icon category-icon-lozenge category-icon-bg--${getIconCategoryClass(Array.isArray(track.component.iconCategory) ? track.component.iconCategory[0] : track.component.iconCategory)}`}><LottieIcon icon={track.component.icon} varKey={track.component.varKey} size={16} /></span>
                                <span className="clip-label">{track.component.label}</span>
                              </div>

                              {/* Right drag handle */}
                              <div 
                                className="clip-resize-handle right"
                                onMouseDown={(e) => handleDragStart(e, track, 'right')}
                                onMouseEnter={(e) => {
                                  console.log('[HANDLE] RIGHT onMouseEnter fired!');
                                  handleMouseEnterHandle(e);
                                }}
                                onMouseLeave={(e) => {
                                  console.log('[HANDLE] RIGHT onMouseLeave fired!');
                                  handleMouseLeaveHandle();
                                }}
                                onMouseOver={(e) => {
                                  e.stopPropagation();
                                  console.log('[HANDLE] RIGHT onMouseOver fired!');
                                  handleMouseEnterHandle(e);
                                }}
                                style={{ cursor: 'ew-resize', pointerEvents: 'auto', zIndex: 999, position: 'absolute' }}
                              />
                              
                              <div className="clip-progress"></div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              });
            })()
          )}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
